import React, { useState, useEffect, useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  ModalBody,
  ModalHeader,
  Col,
  Row,
  Input,
} from 'reactstrap';
import { ReactComponent as CloseButtonSvg } from 'assets/images/location/close-button-svg.svg';
import Button from 'components/App/Button';
import { parseCurrency, unparseCurrency } from 'utils';
import styles from './ModalEditParking.module.scss';

const fallbackParkingServiceSetting = [
  {
    name: 'PARKING_TYPES',
    vi: 'Loại bãi đậu xe',
    en: 'Parking type',
    children: [
      { name: 'INDOOR', vi: 'Trong nhà', en: 'Indoor' },
      { name: 'OUTDOOR', vi: 'Ngoài trời', en: 'Outdoor' },
      { name: 'STREET', vi: 'Ngoài đường', en: 'On-street' },
    ],
  },
  {
    name: 'PARKING_LOCATIONS',
    vi: 'Bãi đậu xe này nằm ở đâu?',
    en: 'Where is this parking located?',
    children: [
      { name: 'ON_SITE', vi: 'Trong khuôn viên', en: 'On-site' },
      { name: 'OFF_SITE', vi: 'Ngoài khuôn viên', en: 'Off-site' },
    ],
  },
  {
    name: 'PARKING_ACCESS_TYPES',
    vi: 'Ai có thể đậu xe ở đây?',
    en: 'Who can park here?',
    children: [
      { name: 'GUESTS_ONLY', vi: 'Chỉ dành cho khách', en: 'Guests only' },
      { name: 'EVERYONE', vi: 'Dành cho tất cả mọi người', en: 'For everyone' },
    ],
  },
  {
    name: 'PARKING_RESERVATION',
    vi: 'Khách đặt chỗ trước',
    en: 'Guest reservation',
    children: [
      { name: 'REQUIRED', vi: 'Bắt buộc', en: 'Required' },
      { name: 'OPTIONAL', vi: 'Không bắt buộc', en: 'Optional' },
    ],
  },
];

const getSettingByName = (parkingSetting, name) => {
  const found = (parkingSetting || []).find((item) => item.name === name);
  if (found && found.children && found.children.length > 0) {
    return found.children;
  }
  return (
    fallbackParkingServiceSetting.find((item) => item.name === name)?.children || []
  );
};

function ModalEditParking(props) {
  const {
    isOpen,
    parkingSetting,
    parking,
    onCancelModal,
    onConfirmModal,
  } = props;

  const [localParking, setLocalParking] = useState({
    hasParking: false,
    parkingTypes: [],
    parkingLocations: [],
    parkingAccessTypes: '',
    parkingReservation: '',
    isPaid: false,
    price: 0,
    accessible: false,
    valet: false,
  });

  const parkingTypesSetting = useMemo(
    () => getSettingByName(parkingSetting, 'PARKING_TYPES'),
    [parkingSetting],
  );
  const parkingLocationsSetting = useMemo(
    () => getSettingByName(parkingSetting, 'PARKING_LOCATIONS'),
    [parkingSetting],
  );
  const parkingAccessTypesSetting = useMemo(
    () => getSettingByName(parkingSetting, 'PARKING_ACCESS_TYPES'),
    [parkingSetting],
  );
  const parkingReservationSetting = useMemo(
    () => getSettingByName(parkingSetting, 'PARKING_RESERVATION'),
    [parkingSetting],
  );

  useEffect(() => {
    if (isOpen) {
      setLocalParking({
        hasParking: parking?.hasParking ?? false,
        parkingTypes: parking?.parkingTypes ?? [],
        parkingLocations: parking?.parkingLocations ?? [],
        parkingAccessTypes: parking?.parkingAccessTypes ?? '',
        parkingReservation: parking?.parkingReservation ?? '',
        isPaid: parking?.isPaid ?? false,
        price: parking?.price ?? 0,
        accessible: parking?.accessible ?? false,
        valet: parking?.valet ?? false,
      });
    }
  }, [isOpen, parking]);

  const handleRadioChange = (name, value) => {
    setLocalParking((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckboxChange = (name, itemValue) => {
    setLocalParking((prev) => {
      const list = prev[name] || [];
      const newList = list.includes(itemValue)
        ? list.filter((v) => v !== itemValue)
        : [...list, itemValue];
      return {
        ...prev,
        [name]: newList,
      };
    });
  };

  const handlePriceChange = (e) => {
    const val = parseCurrency(e.target.value);
    setLocalParking((prev) => ({
      ...prev,
      price: val || 0,
    }));
  };

  const handleSelectAll = () => {
    setLocalParking({
      hasParking: true,
      parkingTypes: parkingTypesSetting.map((item) => item.name),
      parkingLocations: parkingLocationsSetting.map((item) => item.name),
      parkingAccessTypes: parkingAccessTypesSetting[0]?.name || '',
      parkingReservation: parkingReservationSetting[0]?.name || '',
      isPaid: true,
      price: 0,
      accessible: true,
      valet: true,
    });
  };

  const handleClearAll = () => {
    setLocalParking({
      hasParking: false,
      parkingTypes: [],
      parkingLocations: [],
      parkingAccessTypes: '',
      parkingReservation: '',
      isPaid: false,
      price: 0,
      accessible: false,
      valet: false,
    });
  };

  const handleConfirm = () => {
    onConfirmModal(localParking);
  };

  const CloseButton = useMemo(() => (
    <button className={styles.ButtonClose} onClick={onCancelModal} type="button">
      <CloseButtonSvg />
    </button>
  ), [onCancelModal]);

  return (
    <div className={styles.Component}>
      <Modal
        fade
        size="lg"
        isOpen={isOpen}
        toggle={onCancelModal}
        className={styles.Modal}
      >
        <ModalHeader
          toggle={onCancelModal}
          className={styles.ModalHeader}
          close={CloseButton}
        >
          Dịch vụ đậu xe
        </ModalHeader>
        <ModalBody className={styles.ModalBody}>
          <Row className="Block__Container align-items-center">
            <Col md="5" className="Block__Title font-weight-semibold">
              Chỗ nghỉ có chỗ đậu xe không?
              <span className="text-danger">*</span>
            </Col>
            <Col md="7">
              <div className="d-flex gap-4">
                <label className="Block__Checkbox__Wrapper">
                  <Input
                    type="radio"
                    className="Block__Checkbox"
                    checked={localParking.hasParking === true}
                    onChange={() => handleRadioChange('hasParking', true)}
                  />
                  <div className="Block__Checkbox__Name">Có</div>
                </label>
                <label className="Block__Checkbox__Wrapper">
                  <Input
                    type="radio"
                    className="Block__Checkbox"
                    checked={localParking.hasParking === false}
                    onChange={() => handleRadioChange('hasParking', false)}
                  />
                  <div className="Block__Checkbox__Name">Không</div>
                </label>
              </div>
            </Col>
          </Row>

          {localParking.hasParking && (
            <>
              <Row className="Block__Container align-items-center">
                <Col md="5" className="Block__Title">
                  Loại bãi đậu xe
                </Col>
                <Col md="7">
                  <div className="d-flex gap-4 flex-wrap">
                    {parkingTypesSetting.map((item) => (
                      <label key={item.name} className="Block__Checkbox__Wrapper">
                        <Input
                          type="checkbox"
                          className="Block__Checkbox"
                          checked={localParking.parkingTypes.includes(item.name)}
                          onChange={() => handleCheckboxChange('parkingTypes', item.name)}
                        />
                        <div className="Block__Checkbox__Name">{item.vi}</div>
                      </label>
                    ))}
                  </div>
                </Col>
              </Row>

              <Row className="Block__Container align-items-center">
                <Col md="5" className="Block__Title">
                  Bãi đậu xe này nằm ở đâu?
                  <span className="text-danger">*</span>
                </Col>
                <Col md="7">
                  <div className="d-flex gap-4 flex-wrap">
                    {parkingLocationsSetting.map((item) => (
                      <label key={item.name} className="Block__Checkbox__Wrapper">
                        <Input
                          type="checkbox"
                          className="Block__Checkbox"
                          checked={localParking.parkingLocations.includes(item.name)}
                          onChange={() => handleCheckboxChange('parkingLocations', item.name)}
                        />
                        <div className="Block__Checkbox__Name">{item.vi}</div>
                      </label>
                    ))}
                  </div>
                </Col>
              </Row>

              <Row className="Block__Container align-items-center">
                <Col md="5" className="Block__Title">
                  Ai có thể đậu xe ở đây?
                  <span className="text-danger">*</span>
                </Col>
                <Col md="7">
                  <div className="d-flex gap-4">
                    {parkingAccessTypesSetting.map((item) => (
                      <label key={item.name} className="Block__Checkbox__Wrapper">
                        <Input
                          type="radio"
                          className="Block__Checkbox"
                          checked={localParking.parkingAccessTypes === item.name}
                          onChange={() => handleRadioChange('parkingAccessTypes', item.name)}
                        />
                        <div className="Block__Checkbox__Name">{item.vi}</div>
                      </label>
                    ))}
                  </div>
                </Col>
              </Row>

              <Row className="Block__Container align-items-center">
                <Col md="5" className="Block__Title">
                  Khách đặt chỗ trước?
                  <span className="text-danger">*</span>
                </Col>
                <Col md="7">
                  <div className="d-flex gap-4 flex-wrap">
                    {parkingReservationSetting.map((item) => (
                      <label key={item.name} className="Block__Checkbox__Wrapper">
                        <Input
                          type="radio"
                          className="Block__Checkbox"
                          checked={localParking.parkingReservation === item.name}
                          onChange={() => handleRadioChange('parkingReservation', item.name)}
                        />
                        <div className="Block__Checkbox__Name">{item.vi}</div>
                      </label>
                    ))}
                  </div>
                </Col>
              </Row>

              <Row className="Block__Container">
                <Col md="5" className="Block__Title pt-1">
                  Bãi đậu xe này có tính phí không?
                  <span className="text-danger">*</span>
                </Col>
                <Col md="7">
                  <div className="d-flex gap-4 flex-wrap mb-2">
                    <label className="Block__Checkbox__Wrapper">
                      <Input
                        type="radio"
                        className="Block__Checkbox"
                        checked={localParking.isPaid === true}
                        onChange={() => handleRadioChange('isPaid', true)}
                      />
                      <div className="Block__Checkbox__Name">Có</div>
                    </label>
                    <label className="Block__Checkbox__Wrapper">
                      <Input
                        type="radio"
                        className="Block__Checkbox"
                        checked={localParking.isPaid === false}
                        onChange={() => handleRadioChange('isPaid', false)}
                      />
                      <div className="Block__Checkbox__Name">Không</div>
                    </label>
                  </div>
                  {localParking.isPaid && (
                    <div className={styles.PriceInput__Wrapper}>
                      <Input
                        type="text"
                        style={{ paddingRight: '45px' }}
                        placeholder="Vui lòng nhập giá"
                        value={unparseCurrency(localParking.price) || ''}
                        onChange={handlePriceChange}
                      />
                      <span className={styles.PriceInput__Unit}>VNĐ</span>
                    </div>
                  )}
                </Col>
              </Row>

              <Row className="Block__Container align-items-center">
                <Col md="5" className="Block__Title">
                  Bãi đậu xe tiếp cận người khuyết tật
                </Col>
                <Col md="7">
                  <div className="d-flex gap-4">
                    <label className="Block__Checkbox__Wrapper">
                      <Input
                        type="radio"
                        className="Block__Checkbox"
                        checked={localParking.accessible === true}
                        onChange={() => handleRadioChange('accessible', true)}
                      />
                      <div className="Block__Checkbox__Name">Có</div>
                    </label>
                    <label className="Block__Checkbox__Wrapper">
                      <Input
                        type="radio"
                        className="Block__Checkbox"
                        checked={localParking.accessible === false}
                        onChange={() => handleRadioChange('accessible', false)}
                      />
                      <div className="Block__Checkbox__Name">Không</div>
                    </label>
                  </div>
                </Col>
              </Row>

              <Row className="Block__Container align-items-center">
                <Col md="5" className="Block__Title">
                  Nhân viên đậu xe cho khách
                </Col>
                <Col md="7">
                  <div className="d-flex gap-4">
                    <label className="Block__Checkbox__Wrapper">
                      <Input
                        type="radio"
                        className="Block__Checkbox"
                        checked={localParking.valet === true}
                        onChange={() => handleRadioChange('valet', true)}
                      />
                      <div className="Block__Checkbox__Name">Có</div>
                    </label>
                    <label className="Block__Checkbox__Wrapper">
                      <Input
                        type="radio"
                        className="Block__Checkbox"
                        checked={localParking.valet === false}
                        onChange={() => handleRadioChange('valet', false)}
                      />
                      <div className="Block__Checkbox__Name">Không</div>
                    </label>
                  </div>
                </Col>
              </Row>
            </>
          )}

          <div className="Button__Wrapper">
            <div>
              <Button
                isDefault
                classes="Button__SelectAll"
                type="button"
                onClick={handleSelectAll}
              >
                Chọn tất cả
              </Button>

              <Button
                isDefault
                classes="Button__ClearAll"
                type="button"
                onClick={handleClearAll}
              >
                Xóa tất cả lựa chọn
              </Button>
            </div>

            <div>
              <Button
                classes="Button Button__Cancel"
                onClick={onCancelModal}
              >
                Huỷ
              </Button>

              <Button classes="Button Button__Save" onClick={handleConfirm}>
                Xác nhận
              </Button>
            </div>
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
}

ModalEditParking.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  parkingSetting: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      vi: PropTypes.string,
      en: PropTypes.string,
      children: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
          vi: PropTypes.string,
          en: PropTypes.string,
        }),
      ),
    }),
  ),
  parking: PropTypes.shape({
    hasParking: PropTypes.bool,
    parkingTypes: PropTypes.arrayOf(PropTypes.string),
    parkingLocations: PropTypes.arrayOf(PropTypes.string),
    parkingAccessTypes: PropTypes.string,
    parkingReservation: PropTypes.string,
    isPaid: PropTypes.bool,
    price: PropTypes.number,
    accessible: PropTypes.bool,
    valet: PropTypes.bool,
  }),
  onCancelModal: PropTypes.func.isRequired,
  onConfirmModal: PropTypes.func.isRequired,
};

ModalEditParking.defaultProps = {
  parkingSetting: [],
  parking: null,
};

export default memo(ModalEditParking);
